import {
	Box,
	Table,
	AsyncSelect,
	AsyncSelectProps,
	Dropdown,
	Icons,
	Text,
	RelativeTime,
	Container,
	Stack,
} from "@sembark-travel/ui-base"
import { ButtonLink, useLocationQuery } from "@sembark-travel/ui-router"
import { ListView, Search, useSearch } from "@sembark-travel/ui-list"
import { useXHR } from "@sembark-travel/xhr"
import { Fragment, useEffect, useState } from "react"
import { Helmet } from "react-helmet-async"
import { Omit } from "utility-types"
import { generatePath } from "../../router-utils"
import { AddHotelPaymentPreferenceDialog } from "./NewItem"
import { activityPaymentPreferencesXHR as XHR } from "./store"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import pluralize from "pluralize"
import { IPaymentPreference } from "../store"
import { ServiceType } from "../../Supplier"
import { PERMISSIONS, useCheckPermissions } from "../../Auth"

export function List() {
	const [query, setQuery] = useLocationQuery()
	const [params, setParams] = useSearch(query)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	const { hasPermission } = useCheckPermissions()
	return (
		<Fragment>
			<Helmet>
				<title>Activity Payment Preferences List</title>
			</Helmet>
			<Search
				initialParams={params}
				onSearch={(params) => {
					setParams({ ...params, page: 1 })
				}}
				title="Activity Payment Preferences"
				actions={
					<>
						{hasPermission(PERMISSIONS.VIEW_TRANSPORT_SERVICE_PROVIDERS) ? (
							<ButtonLink
								to={generatePath("/supplier-payment-preferences/activity/new")}
								level="primary"
								status="primary"
								anchored
							>
								<Icons.Plus /> Add New
							</ButtonLink>
						) : null}
					</>
				}
			/>
			<ListView<IPaymentPreference>
				pageKey="activity-payment-preferences"
				fetch={(xhr, params) =>
					XHR(xhr).getActivityPaymentPreferences({
						...(params as Record<string, string | number | undefined>),
						include: "hotels_count,created_by,activity_suppliers_count",
					})
				}
				params={params}
				onPageChange={(page) => setParams({ ...params, page })}
				emptyState={() => (
					<Container paddingY="8" textAlign="center">
						<Stack gap="4">
							<Box>
								<Icons.Info size="12" />
							</Box>
							<Stack gap="2" alignItems="center">
								<Text fontSize="md">
									There are currently no Activity Payment Preferences.
								</Text>
							</Stack>
							{hasPermission(PERMISSIONS.VIEW_TRANSPORT_SERVICE_PROVIDERS) ? (
								<Box>
									<ButtonLink
										to={generatePath(
											"/supplier-payment-preferences/activity/new"
										)}
										level="secondary"
									>
										<Icons.Plus />
										Add Activity Payment Preference
									</ButtonLink>
								</Box>
							) : null}
						</Stack>
					</Container>
				)}
			>
				{({ items: paymentPreferences, refresh }) => (
					<Table
						headers={["Description", "Suppliers", "Created By", ""]}
						bordered
						striped
						alignCols={{ 1: "center", 3: "right" }}
						rows={paymentPreferences.map((paymentPreference) => [
							paymentPreference.name,
							paymentPreference.activity_suppliers_count ?? 0,
							<Text>
								{paymentPreference.created_by?.name || "You"}{" "}
								<RelativeTime
									timestamp={paymentPreference.created_at}
									color="muted"
									fontSize="sm"
								/>
							</Text>,
							<ActivityPaymentPreferenceActionsDropdown
								paymentPreference={paymentPreference}
								refresh={refresh}
							/>,
						])}
					/>
				)}
			</ListView>
		</Fragment>
	)
}

function ActivityPaymentPreferenceActionsDropdown({
	paymentPreference,
	refresh,
}: Readonly<{
	paymentPreference: IPaymentPreference
	refresh: () => void
}>) {
	const xhr = useXHR()
	const { hasPermission } = useCheckPermissions()
	return (
		<Dropdown alignRight>
			<Dropdown.ToggleButton level="tertiary" size="sm">
				<Icons.DotsVertical />
			</Dropdown.ToggleButton>
			<Dropdown.Menu>
				{hasPermission(
					PERMISSIONS.MANAGE_TRIP_OWNERS,
					PERMISSIONS.VIEW_TRANSPORT_SERVICE_PROVIDERS
				) ? (
					<Dropdown.MenuItem
						onClick={() => {
							if (
								window.confirm(
									`This is an IRREVERSIBLE action.${
										(paymentPreference.activity_suppliers_count ?? 0)
											? ` It will also be removed from ${pluralize(
													"activity",
													paymentPreference.activity_suppliers_count ?? 0,
													true
												)}.`
											: ""
									}\n\nAre you sure you want to delete this Payment Preference?`
								)
							) {
								xhr
									.delete(
										`/supplier-payment-preferences/${paymentPreference.id}`,
										{
											params: {
												service_type: ServiceType.ActivityBookingServices,
											},
										}
									)
									.then(() => {
										showSnackbar("Deleted successfully.")
										refresh()
									})
									.catch((e) => {
										const error = e as Error
										alert(
											error.message ||
												"Something went wrong. Please try after sometime"
										)
									})
							}
						}}
						color="danger"
					>
						<Icons.Trash /> Delete
					</Dropdown.MenuItem>
				) : null}
			</Dropdown.Menu>
		</Dropdown>
	)
}

type SelectActivityPaymentPreferencesProps = Omit<AsyncSelectProps, "fetch">

export function SelectActivityPaymentPreferences({
	...otherProps
}: Readonly<SelectActivityPaymentPreferencesProps>) {
	const xhr = useXHR()
	const [newName, setNewName] = useState("")
	return (
		<Fragment>
			<AsyncSelect
				multiple
				onCreateNew={(query: string) => {
					setNewName(query)
				}}
				optionRenderer={({ option, created }) =>
					created ? <Box>Add "{option.name}"</Box> : <Box>{option.name}</Box>
				}
				{...otherProps}
				fetch={(q) =>
					XHR(xhr)
						.getActivityPaymentPreferences({ q })
						.then((resp) => resp.data)
				}
			/>
			<AddHotelPaymentPreferenceDialog
				open={!!newName}
				onClose={() => {
					setNewName("")
				}}
			/>
		</Fragment>
	)
}
